/**
 * Resolves an assignment block's `asset.url` to something an `<img>` can render. The value is either
 * an external URL (rendered as-is) or an object path in a private bucket, told apart by the absence
 * of a scheme. Paths are exchanged for signed links: module cache → the store's `mediaUrls` map
 * (while `media_expires_at` holds) → a fresh `?path=` re-sign. Signed URLs are render-only, never persisted.
 */
import { computed, ref, watch } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { Dayjs } from 'dayjs'
import type { AssignmentMedia } from './interfaces'

import { useProfileStore } from '@entities/viewer'
import { useDayjs } from '@shared/lib/format'

import { useAssignmentEditorStore } from './store'
import { useAssignmentTemplateMediaLinkApi } from '../../api/assignment-templates/media-link.hook'
import { isAssignmentMediaPath } from '../../lib/assignments/assignment-media'

const dayjs = useDayjs()

const REFRESH_MARGIN_MS = 60 * 1000

/** For a missing or unparseable expiry. */
const FALLBACK_TTL_MS = 5 * 60 * 1000

interface CachedLink {
  url: string
  expiresAtMs: number
}

/** Module-level so two components resolving the same object share one request. */
const linkCache = new Map<string, CachedLink>()

/** The wire shape of `media_expires_at` / `expires_at`: no offset, so the zone has to be supplied. */
const NAIVE_DATETIME = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/

/** `null` when the profile's zone is not one Intl knows; the caller falls back to the TTL. */
const parseInViewTimezone = (value: string): Dayjs | null => {
  try {
    return dayjs.tz(value, useProfileStore().getTimezone)
  } catch {
    return null
  }
}

/** Expiry strings are naive `Y-m-d H:i:s` in the admin's `view_timezone`, so they are read in that
 *  zone — a browser-local parse would land hours off and keep serving an expired link. A value
 *  that carries its own offset is taken as-is. */
const expiryToMs = (expiresAt: string | null | undefined): number => {
  if (!expiresAt) {
    return Date.now() + FALLBACK_TTL_MS
  }
  const parsed = NAIVE_DATETIME.test(expiresAt) ? parseInViewTimezone(expiresAt) : dayjs(expiresAt)

  return parsed?.isValid() ? parsed.valueOf() : Date.now() + FALLBACK_TTL_MS
}

const isFresh = (expiresAtMs: number): boolean => expiresAtMs - REFRESH_MARGIN_MS > Date.now()

export const rememberAssignmentMediaLink = (media: AssignmentMedia): void => {
  linkCache.set(media.path, { url: media.url, expiresAtMs: expiryToMs(media.expires_at) })
}

export const rememberAssignmentMediaUrl = (
  path: string,
  url: string,
  expiresAt: string | null | undefined,
): void => {
  linkCache.set(path, { url, expiresAtMs: expiryToMs(expiresAt) })
}

/** Test seam. */
export const clearAssignmentMediaLinkCache = (): void => {
  linkCache.clear()
}

export interface UseAssignmentMediaSrcReturn {
  /** Empty while there is nothing to show or a path is resolving. */
  refSrc: ComputedRef<string>
  refIsUploaded: ComputedRef<boolean>
  refIsResolving: Ref<boolean>
  refError: Ref<string | null>
}

export const useAssignmentMediaSrc = (
  value: () => string | undefined,
): UseAssignmentMediaSrcReturn => {
  const store = useAssignmentEditorStore()

  const refValue = computed<string>(() => {
    const raw = value()

    return typeof raw === 'string' ? raw.trim() : ''
  })

  const refIsUploaded = computed<boolean>(() => isAssignmentMediaPath(refValue.value))

  const resolvedUrl = ref<string>('')
  const refIsResolving = ref<boolean>(false)
  const refError = ref<string | null>(null)

  const refSrc = computed<string>(() => (refIsUploaded.value ? resolvedUrl.value : refValue.value))

  /** Guards against out-of-order responses. */
  let requestSeq = 0

  const resolve = async (): Promise<void> => {
    const path = refValue.value
    const templateId = store.getTemplateId
    const token = ++requestSeq

    refError.value = null

    if (!isAssignmentMediaPath(path)) {
      resolvedUrl.value = ''
      refIsResolving.value = false
      return
    }

    // No row loaded yet; the watcher fires again when it is. A request this supersedes exits at its
    // token check without touching the flag, so it is cleared here.
    if (!templateId) {
      resolvedUrl.value = ''
      refIsResolving.value = false
      return
    }

    const cached = linkCache.get(path)

    if (cached && isFresh(cached.expiresAtMs)) {
      resolvedUrl.value = cached.url
      refIsResolving.value = false
      return
    }

    // All URLs in a response share one expiry.
    const responseUrl = store.getMediaUrl(path)

    if (responseUrl && isFresh(expiryToMs(store.getMediaExpiresAt))) {
      rememberAssignmentMediaUrl(path, responseUrl, store.getMediaExpiresAt)
      resolvedUrl.value = responseUrl
      refIsResolving.value = false
      return
    }

    refIsResolving.value = true

    const media = await useAssignmentTemplateMediaLinkApi({
      templateId,
      path,
      onError: (message) => {
        if (token === requestSeq) refError.value = message
      },
    })

    if (token !== requestSeq) return

    refIsResolving.value = false

    if (!media) {
      resolvedUrl.value = ''
      // 404 = object gone or path out of prefix; not retryable.
      refError.value ??= 'This image could not be loaded. Upload it again.'
      return
    }

    rememberAssignmentMediaLink(media)
    resolvedUrl.value = media.url
  }

  watch(
    [refValue, () => store.getTemplateId, () => store.getMediaExpiresAt],
    () => {
      void resolve()
    },
    { immediate: true },
  )

  return {
    refSrc,
    refIsUploaded,
    refIsResolving,
    refError,
  }
}
